import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  limit,
  onSnapshot
} from 'firebase/firestore';

/**
 * Enhanced Notification Service for real-time platform notifications
 */

/**
 * Create a notification
 * @param {string} userId - User ID to notify
 * @param {string} title - Notification title
 * @param {string} message - Notification message
 * @param {string} type - Notification type (message, assignment, rating, application, etc.)
 * @param {Object} metadata - Additional metadata (projectId, senderId, etc.)
 * @returns {Promise<string>} - Notification ID
 */
export const createNotification = async (userId, title, message, type = 'general', metadata = {}) => {
  try {
    const notification = {
      userId,
      title,
      message,
      type,
      isRead: false,
      isArchived: false,
      priority: metadata.priority || 'normal', // low, normal, high, urgent
      createdAt: serverTimestamp(),
      
      // Metadata for different notification types
      metadata: {
        projectId: metadata.projectId || null,
        senderId: metadata.senderId || null,
        senderName: metadata.senderName || null,
        senderType: metadata.senderType || null, // student, company
        relatedId: metadata.relatedId || null, // message ID, assignment ID, etc.
        actionUrl: metadata.actionUrl || null,
        ...metadata
      }
    };

    const notificationRef = await addDoc(collection(db, 'notifications'), notification);
    return notificationRef.id;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw new Error('Failed to create notification: ' + error.message);
  }
};

/**
 * Get notifications for a user
 * @param {string} userId - User ID
 * @param {number} limitCount - Number of notifications to fetch
 * @param {boolean} unreadOnly - Fetch only unread notifications
 * @returns {Promise<Array>} - Array of notifications
 */
export const getUserNotifications = async (userId, limitCount = 20, unreadOnly = false) => {
  try {
    let notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('isArchived', '==', false),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    if (unreadOnly) {
      notificationsQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('isArchived', '==', false),
        where('isRead', '==', false),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
    }

    const querySnapshot = await getDocs(notificationsQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw new Error('Failed to fetch notifications: ' + error.message);
  }
};

/**
 * Subscribe to real-time notifications for a user
 * @param {string} userId - User ID
 * @param {Function} callback - Callback function to handle notifications
 * @param {number} limitCount - Number of notifications to fetch
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToNotifications = (userId, callback, limitCount = 20) => {
  try {
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('isArchived', '==', false),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    return onSnapshot(notificationsQuery, (snapshot) => {
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      callback(notifications);
    });
  } catch (error) {
    console.error('Error subscribing to notifications:', error);
    return () => {}; // Return empty unsubscribe function
  }
};

/**
 * Mark notification as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    await updateDoc(doc(db, 'notifications', notificationId), {
      isRead: true,
      readAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw new Error('Failed to mark notification as read: ' + error.message);
  }
};

/**
 * Mark all notifications as read for a user
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const unreadQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('isRead', '==', false)
    );

    const unreadSnapshot = await getDocs(unreadQuery);
    
    const updatePromises = unreadSnapshot.docs.map(doc => 
      updateDoc(doc.ref, {
        isRead: true,
        readAt: serverTimestamp()
      })
    );

    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw new Error('Failed to mark all notifications as read: ' + error.message);
  }
};

/**
 * Archive notification
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export const archiveNotification = async (notificationId) => {
  try {
    await updateDoc(doc(db, 'notifications', notificationId), {
      isArchived: true,
      archivedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error archiving notification:', error);
    throw new Error('Failed to archive notification: ' + error.message);
  }
};

/**
 * Delete notification
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export const deleteNotification = async (notificationId) => {
  try {
    await deleteDoc(doc(db, 'notifications', notificationId));
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw new Error('Failed to delete notification: ' + error.message);
  }
};

/**
 * Get unread notification count for a user
 * @param {string} userId - User ID
 * @returns {Promise<number>} - Unread count
 */
export const getUnreadNotificationCount = async (userId) => {
  try {
    const unreadQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('isRead', '==', false),
      where('isArchived', '==', false)
    );

    const unreadSnapshot = await getDocs(unreadQuery);
    return unreadSnapshot.size;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};

/**
 * Create message notification
 * @param {string} recipientId - Recipient user ID
 * @param {string} senderId - Sender user ID
 * @param {string} senderName - Sender name
 * @param {string} senderType - Sender type (student/company)
 * @param {string} projectId - Project ID
 * @param {string} projectTitle - Project title
 * @param {string} messagePreview - Preview of message content
 * @returns {Promise<string>} - Notification ID
 */
export const createMessageNotification = async (
  recipientId, 
  senderId, 
  senderName, 
  senderType, 
  projectId, 
  projectTitle, 
  messagePreview
) => {
  const truncatedPreview = messagePreview.length > 50 
    ? messagePreview.substring(0, 50) + '...' 
    : messagePreview;

  return await createNotification(
    recipientId,
    `New message from ${senderName}`,
    `"${truncatedPreview}"`,
    'message',
    {
      senderId,
      senderName,
      senderType,
      projectId,
      projectTitle,
      priority: 'normal',
      actionUrl: `/project/${projectId}`
    }
  );
};

/**
 * Create assignment notification
 * @param {string} studentId - Student ID
 * @param {string} companyName - Company name
 * @param {string} assignmentTitle - Assignment title
 * @param {string} projectId - Project ID
 * @param {string} type - Assignment notification type (new, feedback, approved, etc.)
 * @returns {Promise<string>} - Notification ID
 */
export const createAssignmentNotification = async (
  studentId, 
  companyName, 
  assignmentTitle, 
  projectId, 
  type = 'new'
) => {
  const notificationConfig = {
    new: {
      title: 'New Assignment Available',
      message: `You have a new assignment: "${assignmentTitle}"`,
      priority: 'high'
    },
    feedback: {
      title: 'Assignment Feedback Received',
      message: `You received feedback on "${assignmentTitle}"`,
      priority: 'normal'
    },
    approved: {
      title: 'Assignment Approved!',
      message: `Your assignment "${assignmentTitle}" has been approved`,
      priority: 'normal'
    },
    revision: {
      title: 'Assignment Revision Requested',
      message: `Revision requested for "${assignmentTitle}"`,
      priority: 'high'
    }
  };

  const config = notificationConfig[type] || notificationConfig.new;

  return await createNotification(
    studentId,
    config.title,
    config.message,
    'assignment',
    {
      senderName: companyName,
      senderType: 'company',
      projectId,
      assignmentTitle,
      priority: config.priority,
      actionUrl: `/project/${projectId}`
    }
  );
};

/**
 * Create rating notification
 * @param {string} studentId - Student ID
 * @param {string} companyName - Company name
 * @param {number} rating - Rating given
 * @param {string} projectId - Project ID
 * @param {string} projectTitle - Project title
 * @returns {Promise<string>} - Notification ID
 */
export const createRatingNotification = async (
  studentId, 
  companyName, 
  rating, 
  projectId, 
  projectTitle
) => {
  const stars = '★'.repeat(Math.floor(rating));
  
  return await createNotification(
    studentId,
    'You received a new rating!',
    `${companyName} rated you ${rating}/5 ${stars} for "${projectTitle}"`,
    'rating',
    {
      senderName: companyName,
      senderType: 'company',
      projectId,
      projectTitle,
      rating,
      priority: 'normal',
      actionUrl: `/project/${projectId}`
    }
  );
};

/**
 * Create application notification
 * @param {string} companyId - Company ID
 * @param {string} studentName - Student name
 * @param {string} projectId - Project ID
 * @param {string} projectTitle - Project title
 * @param {string} type - Application type (new, accepted, rejected)
 * @returns {Promise<string>} - Notification ID
 */
export const createApplicationNotification = async (
  recipientId, 
  studentName, 
  projectId, 
  projectTitle, 
  type = 'new'
) => {
  const notificationConfig = {
    new: {
      title: 'New Project Application',
      message: `${studentName} applied to "${projectTitle}"`,
      priority: 'normal'
    },
    accepted: {
      title: 'Application Accepted!',
      message: `Your application to "${projectTitle}" has been accepted`,
      priority: 'high'
    },
    rejected: {
      title: 'Application Update',
      message: `Update on your application to "${projectTitle}"`,
      priority: 'normal'
    }
  };

  const config = notificationConfig[type] || notificationConfig.new;

  return await createNotification(
    recipientId,
    config.title,
    config.message,
    'application',
    {
      studentName,
      projectId,
      projectTitle,
      priority: config.priority,
      actionUrl: type === 'new' ? `/company/project/${projectId}` : `/project/${projectId}`
    }
  );
};

// Utility functions
export const getNotificationIcon = (type) => {
  const icons = {
    message: '•',
    assignment: '•',
    rating: '★',
    application: '•',
    system: '•',
    general: '•'
  };
  return icons[type] || '•';
};

export const getNotificationColor = (type, priority) => {
  if (priority === 'urgent') return '#dc2626';
  if (priority === 'high') return '#ea580c';
  
  const colors = {
    message: '#3b82f6',
    assignment: '#8b5cf6',
    rating: '#f59e0b',
    application: '#10b981',
    system: '#6b7280',
    general: '#6366f1'
  };
  return colors[type] || '#6366f1';
};

export const formatNotificationTime = (timestamp) => {
  if (!timestamp) return 'Now';
  
  try {
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 60000);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  } catch (e) {
    return 'Recently';
  }
}; 